import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import './Puzzles.css';

const Puzzles = ({ onBack, onStartPuzzle, onOpenEndgameTrainer }) => {
  const [puzzles, setPuzzles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [unsolvedOnly, setUnsolvedOnly] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPuzzles();
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDifficulty, unsolvedOnly]);

  const loadPuzzles = async () => {
    try {
      setLoading(true);
      const difficulty = selectedDifficulty === 'all' ? null : selectedDifficulty;
      const puzzlesData = await api.getPuzzles(difficulty, 20, unsolvedOnly);
      setPuzzles(puzzlesData);
    } catch (err) {
      console.error('Error loading puzzles:', err);
      setError('Failed to load puzzles');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await api.getPuzzleStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return '#4caf50';
      case 'medium': return '#ff9800';
      case 'hard': return '#f44336';
      default: return '#999';
    }
  };

  const getDifficultyIcon = (difficulty) => {
    switch (difficulty) {
      case 'easy': return '⭐';
      case 'medium': return '⭐⭐';
      case 'hard': return '⭐⭐⭐';
      default: return '❓';
    }
  };

  return (
    <div className="puzzles-container">
      <div className="puzzles-header">
        <button onClick={onBack} className="back-button">
          ← Back to Menu
        </button>
        <h1>Tactical Puzzles</h1>
        <p className="puzzles-subtitle">Improve your tactical skills with these challenges!</p>
      </div>

      {onOpenEndgameTrainer && (
        <button
          className="endgame-trainer-cta"
          onClick={onOpenEndgameTrainer}
        >
          🏰 Endgame Trainer — random K+material vs K positions, endless
        </button>
      )}

      {stats && (
        <div className="puzzles-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.puzzleRating || 1200}</div>
            <div className="stat-label">Puzzle Rating</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.completedCount || 0}</div>
            <div className="stat-label">Solved</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.totalAttempts || 0}</div>
            <div className="stat-label">Attempts</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {stats.avgTime ? Math.round(stats.avgTime) + 's' : 'N/A'}
            </div>
            <div className="stat-label">Avg Time</div>
          </div>
        </div>
      )}

      <div className="puzzles-filters">
        <button
          className={`filter-button ${selectedDifficulty === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedDifficulty('all')}
        >
          All Puzzles
        </button>
        <button
          className={`filter-button ${selectedDifficulty === 'easy' ? 'active' : ''}`}
          onClick={() => setSelectedDifficulty('easy')}
        >
          ⭐ Easy
        </button>
        <button
          className={`filter-button ${selectedDifficulty === 'medium' ? 'active' : ''}`}
          onClick={() => setSelectedDifficulty('medium')}
        >
          ⭐⭐ Medium
        </button>
        <button
          className={`filter-button ${selectedDifficulty === 'hard' ? 'active' : ''}`}
          onClick={() => setSelectedDifficulty('hard')}
        >
          ⭐⭐⭐ Hard
        </button>
        <label className={`filter-toggle ${unsolvedOnly ? 'active' : ''}`}>
          <input
            type="checkbox"
            checked={unsolvedOnly}
            onChange={(e) => setUnsolvedOnly(e.target.checked)}
          />
          Unsolved only
        </label>
      </div>

      {error && <div className="puzzles-error">{error}</div>}

      {loading ? (
        <div className="puzzles-loading">Loading puzzles...</div>
      ) : (
        <div className="puzzles-grid">
          {puzzles.map((puzzle) => {
            const done = !!puzzle.completed_by_user;
            return (
              <div key={puzzle.id} className={`puzzle-card${done ? ' puzzle-card-completed' : ''}`}>
                <div className="puzzle-header">
                  {done ? (
                    <span className="puzzle-completed-badge" title="Solved">✓</span>
                  ) : (
                    <span
                      className="puzzle-difficulty"
                      style={{ color: getDifficultyColor(puzzle.difficulty) }}
                    >
                      {getDifficultyIcon(puzzle.difficulty)}
                    </span>
                  )}
                  <span className="puzzle-rating">Rating: {puzzle.rating}</span>
                </div>
                <h3 className="puzzle-title">{puzzle.title}</h3>
                <p className="puzzle-description">{puzzle.description}</p>
                <div className="puzzle-tags">
                  {puzzle.tags && puzzle.tags.split(',').map((tag, idx) => (
                    <span key={idx} className="puzzle-tag">{tag.trim()}</span>
                  ))}
                </div>
                <button
                  className="solve-button"
                  onClick={() => onStartPuzzle(puzzle.id)}
                >
                  {done ? 'Play again →' : 'Solve Puzzle →'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!loading && puzzles.length === 0 && (
        <div className="no-puzzles">
          {unsolvedOnly ? (
            <>
              <p>🎉 You've solved all puzzles at this difficulty!</p>
              <p>Try another difficulty or uncheck "Unsolved only".</p>
            </>
          ) : (
            <>
              <p>No puzzles found for this difficulty level.</p>
              <p>Try selecting a different filter!</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Puzzles;
